import { api, setToken } from './client';
import type {
  AnalyticsResponse,
  CreateSupportTicketRequest,
  SupportSummary,
  SupportTicketDetail,
  SupportTicketListResponse,
  SupportTicketUpdateRequest,
  ApiLogDetail,
  ApiLogPage,
  ClientDecisionRule,
  DecisionRule,
  MyProfile,
  UpdateMyProfileRequest,
  ChangePasswordRequest,
  PasswordOperationResponse,
  ClientApiKeyOperationResponse,
  ClientCreditOperationResponse,
  ClientDetail,
  ClientListResponse,
  ClientOperationResponse,
  ChangeClientPlanRequest,
  ChangeClientStatusRequest,
  CustomPlanTerms,
  CreateClientRequest,
  UpdateClientRequest,
  AuditLogListResponse,
  ComplianceResponse,
  CreateUserRequest,
  DashboardResponse,
  DemoExtractResponse,
  DemoUsageResponse,
  DocumentListResponse,
  FraudAlertDetail,
  FraudAlertListResponse,
  FraudAlertStatusRequest,
  FraudDashboardResponse,
  LoginRequest,
  LoginResponse,
  LoginResult,
  TwoFactorChallengeResponse,
  AuthenticatorSetupResponse,
  CurrentUser,
  ReviewDecisionRequest,
  ToggleUserResponse,
  UpdateUserRequest,
  UserListResponse,
  UserProfileResponse,
  ApiKeyListItem,
  ApiKeyOperationResponse,
  AutoTopUpRequest,
  CheckoutSessionResponse,
  CompanyResponse,
  CreateApiKeyRequest,
  BillingSettings,
  EmailSettings,
  NotificationPreferences,
  IntegrationSettings,
  IntegrationSettingsInput,
  BrandingInput,
  BrandingSettings,
  PlanListItem,
  PlatformSettingsResponse,
  SecuritySettings,
  FaceMatchSettings,
  UpdatePlanRequest,
  SimpleOperationResponse,
  CreditOperationResponse,
  CreditPageResponse,
  InvoiceListItem,
  PurchaseCreditsRequest,
  VerificationDetailResponse,
  VerificationListResponse,
  VerificationEvent,
  SignupCompletion,
  SignupOptions,
  SignupRequest,
  SignupResponse,
  KybSubmitRequest,
  OperationMessageResponse,
} from './types';

// -- Auth ---------------------------------------------------------------------

export async function login(request: LoginRequest): Promise<LoginResult> {
  const { data } = await api.post<LoginResult>('/api/auth/login', request);
  return data;
}

/** Second sign-in step - the texted or emailed code. */
export async function loginTwoFactor(
  challenge: string,
  code: string,
  rememberMe: boolean,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/api/auth/login/2fa', { challenge, code, rememberMe });
  return data;
}

export async function resendTwoFactorCode(challenge: string): Promise<TwoFactorChallengeResponse> {
  const { data } = await api.post<TwoFactorChallengeResponse>('/api/auth/login/2fa/resend', { challenge });
  return data;
}

/** "Email me a code instead" - for a text or authenticator-app step. */
export async function emailCodeInstead(challenge: string): Promise<TwoFactorChallengeResponse> {
  const { data } = await api.post<TwoFactorChallengeResponse>('/api/auth/login/2fa/email', { challenge });
  return data;
}

export async function forgotPassword(email: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/forgot-password', { email });
  return data;
}

export async function validateResetToken(token: string): Promise<boolean> {
  const { data } = await api.get<{ valid: boolean }>('/api/auth/reset-password/validate', {
    params: { token },
  });
  return data.valid;
}

export async function resetPassword(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/reset-password', {
    token,
    newPassword,
    confirmPassword,
  });
  return data;
}

export async function verifyEmail(token: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/verify-email', { token });
  return data;
}

export async function resendVerification(email: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/resend-verification', { email });
  return data;
}

export async function startTwoFactor(phone: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/two-factor/start', { phone });
  return data;
}

export async function confirmTwoFactor(code: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/two-factor/confirm', { code });
  return data;
}

/** Starts authenticator-app setup: the QR code and key. Nothing changes until confirmed. */
export async function startAuthenticator(): Promise<AuthenticatorSetupResponse> {
  const { data } = await api.post<AuthenticatorSetupResponse>('/api/auth/two-factor/authenticator/start');
  return data;
}

/** A code from the app; on success the app becomes the sign-in method. */
export async function confirmAuthenticator(code: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/two-factor/authenticator/confirm', {
    code,
  });
  return data;
}

export async function cancelAuthenticator(): Promise<void> {
  await api.post('/api/auth/two-factor/authenticator/cancel');
}

/** Turns two-step sign-in on with codes by email (replacing other methods). */
export async function enableEmailTwoFactor(currentPassword: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/two-factor/email', {
    currentPassword,
  });
  return data;
}

export async function disableTwoFactor(currentPassword: string): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/two-factor/disable', {
    currentPassword,
  });
  return data;
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  const { data } = await api.get<CurrentUser>('/api/auth/me');
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/api/auth/logout');
}

// -- Dashboard ----------------------------------------------------------------

export async function fetchDashboard(): Promise<DashboardResponse> {
  const { data } = await api.get<DashboardResponse>('/api/dashboard');
  return data;
}

// -- Verifications ------------------------------------------------------------

export interface VerificationListQuery {
  page: number;
  status?: string;
  type?: string;
  search?: string;
  /** yyyy-mm-dd, UTC, inclusive. */
  from?: string;
  /** yyyy-mm-dd, UTC, inclusive. */
  to?: string;
}

export async function fetchVerifications(
  query: VerificationListQuery,
): Promise<VerificationListResponse> {
  const { data } = await api.get<VerificationListResponse>('/api/verifications', {
    params: {
      page: query.page,
      status: query.status || undefined,
      type: query.type || undefined,
      search: query.search || undefined,
      from: query.from || undefined,
      to: query.to || undefined,
    },
  });
  return data;
}

export async function fetchVerificationDetail(id: number): Promise<VerificationDetailResponse> {
  const { data } = await api.get<VerificationDetailResponse>(`/api/verifications/${id}`);
  return data;
}

export async function submitReviewDecision(
  id: number,
  request: ReviewDecisionRequest,
): Promise<void> {
  await api.post(`/api/verifications/${id}/review`, request);
}

/** Downloads the raw Vision payload for one document (SuperAdmin only) as a Blob. */
export async function fetchRawOcrBlob(sessionId: number, documentId: number): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `/api/verifications/${sessionId}/documents/${documentId}/raw-ocr`,
    { responseType: 'blob' },
  );
  return data;
}

// -- Documents ----------------------------------------------------------------

export interface DocumentListQuery {
  page: number;
  type?: string;
  status?: string;
}

export async function fetchDocuments(query: DocumentListQuery): Promise<DocumentListResponse> {
  const { data } = await api.get<DocumentListResponse>('/api/documents', {
    params: {
      page: query.page,
      type: query.type || undefined,
      status: query.status || undefined,
    },
  });
  return data;
}

// -- Fraud --------------------------------------------------------------------

export async function fetchFraudDashboard(): Promise<FraudDashboardResponse> {
  const { data } = await api.get<FraudDashboardResponse>('/api/fraud/dashboard');
  return data;
}

export interface FraudAlertQuery {
  page: number;
  severity?: string;
  status?: string;
  /** A FraudAlertType name. */
  type?: string;
}

/** One alert with evidence, history (SuperAdmin) and the statuses it may move to. */
export async function fetchFraudAlert(id: number): Promise<FraudAlertDetail> {
  const { data } = await api.get<FraudAlertDetail>(`/api/fraud/alerts/${id}`);
  return data;
}

/** SuperAdmin only. Never changes the verification. */
export async function changeFraudAlertStatus(id: number, body: FraudAlertStatusRequest): Promise<void> {
  await api.patch(`/api/fraud/alerts/${id}`, body);
}

/** Moves every unresolved alert of one verification (SuperAdmin only). */
export async function changeVerificationAlerts(
  sessionId: number,
  body: FraudAlertStatusRequest,
): Promise<{ changed: number }> {
  const { data } = await api.post<{ changed: number }>(`/api/fraud/verifications/${sessionId}/alerts/status`, body);
  return data;
}

export async function fetchFraudAlerts(query: FraudAlertQuery): Promise<FraudAlertListResponse> {
  const { data } = await api.get<FraudAlertListResponse>('/api/fraud/alerts', {
    params: {
      page: query.page,
      severity: query.severity || undefined,
      status: query.status || undefined,
      type: query.type || undefined,
    },
  });
  return data;
}

// -- Audit logs ---------------------------------------------------------------

export interface AuditLogQuery {
  page: number;
  severity?: string;
  entityType?: string;
  search?: string;
}

export async function fetchAuditLogs(query: AuditLogQuery): Promise<AuditLogListResponse> {
  const { data } = await api.get<AuditLogListResponse>('/api/audit-logs', {
    params: {
      page: query.page,
      severity: query.severity || undefined,
      entityType: query.entityType || undefined,
      search: query.search || undefined,
    },
  });
  return data;
}

// -- API logs -----------------------------------------------------------------

export interface ApiLogQuery {
  page: number;
  from?: string;
  to?: string;
  path?: string;
  status?: string;
  environment?: string;
  verificationId?: string;
  requestId?: string;
  kind?: string;
}

export async function fetchApiLogs(query: ApiLogQuery): Promise<ApiLogPage> {
  const { data } = await api.get<ApiLogPage>('/api/api-logs', {
    params: {
      page: query.page,
      from: query.from || undefined,
      to: query.to || undefined,
      path: query.path || undefined,
      status: query.status || undefined,
      environment: query.environment || undefined,
      verificationId: query.verificationId || undefined,
      requestId: query.requestId || undefined,
      kind: query.kind || undefined,
    },
  });
  return data;
}

/** One verification's Event timeline, newest first (VerificationEvents, Core). */
export async function fetchVerificationEvents(verificationId: number): Promise<VerificationEvent[]> {
  const { data } = await api.get<VerificationEvent[]>(`/api/verifications/${verificationId}/events`);
  return data;
}

export async function fetchApiLog(id: number): Promise<ApiLogDetail> {
  const { data } = await api.get<ApiLogDetail>(`/api/api-logs/${id}`);
  return data;
}

// -- Reports ------------------------------------------------------------------

export async function fetchAnalytics(): Promise<AnalyticsResponse> {
  const { data } = await api.get<AnalyticsResponse>('/api/reports/analytics');
  return data;
}

export async function fetchCompliance(
  startDate?: string,
  endDate?: string,
): Promise<ComplianceResponse> {
  const { data } = await api.get<ComplianceResponse>('/api/reports/compliance', {
    params: { startDate: startDate || undefined, endDate: endDate || undefined },
  });
  return data;
}

// -- Users --------------------------------------------------------------------

/** With clientId, only that client's users (Client detail → Team). */
export async function fetchUsers(clientId?: number): Promise<UserListResponse> {
  const { data } = await api.get<UserListResponse>('/api/users', { params: { clientId } });
  return data;
}

export async function fetchUserProfile(id: number): Promise<UserProfileResponse> {
  const { data } = await api.get<UserProfileResponse>(`/api/users/${id}`);
  return data;
}

export async function createUser(request: CreateUserRequest): Promise<UserProfileResponse> {
  const { data } = await api.post<UserProfileResponse>('/api/users', request);
  return data;
}

export async function updateUser(
  id: number,
  request: UpdateUserRequest,
): Promise<UserProfileResponse> {
  const { data } = await api.put<UserProfileResponse>(`/api/users/${id}`, request);
  return data;
}

export async function toggleUserActive(id: number): Promise<ToggleUserResponse> {
  const { data } = await api.post<ToggleUserResponse>(`/api/users/${id}/toggle-active`);
  return data;
}

// -- Images -------------------------------------------------------------------

/**
 * Document images are bearer-protected, so they cannot be loaded through a plain
 * <img src>. Callers fetch the bytes here and wrap them in an object URL.
 */
export async function fetchImageBlob(imageId: string): Promise<Blob> {
  const { data } = await api.get<Blob>(`/api/images/${imageId}`, { responseType: 'blob' });
  return data;
}

// -- Demo extraction ----------------------------------------------------------

export interface DemoExtractInput {
  /** Front side, read by OCR. Optional when a back image is supplied. */
  frontImage?: File | null;
  /** Back side, decoded locally as a PDF417 barcode. Optional when a front image is supplied. */
  backImage?: File | null;
  country?: string;
  documentType?: string;
}

export async function runDemoExtraction(input: DemoExtractInput): Promise<DemoExtractResponse> {
  const form = new FormData();
  if (input.frontImage) form.append('frontImage', input.frontImage);
  if (input.backImage) form.append('backImage', input.backImage);
  if (input.country) form.append('country', input.country);
  if (input.documentType) form.append('documentType', input.documentType);

  const { data } = await api.post<DemoExtractResponse>('/api/demo/extract', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function fetchDemoUsage(): Promise<DemoUsageResponse> {
  const { data } = await api.get<DemoUsageResponse>('/api/demo/usage');
  return data;
}

// -- Billing / credits ---------------------------------------------------------

export async function fetchCredits(): Promise<CreditPageResponse> {
  const { data } = await api.get<CreditPageResponse>('/api/billing/credits');
  return data;
}

export async function purchaseCredits(
  request: PurchaseCreditsRequest,
): Promise<CreditOperationResponse> {
  const { data } = await api.post<CreditOperationResponse>(
    '/api/billing/credits/purchase',
    request,
  );
  return data;
}

export async function saveAutoTopUp(
  request: AutoTopUpRequest,
): Promise<CreditOperationResponse> {
  const { data } = await api.put<CreditOperationResponse>(
    '/api/billing/credits/auto-top-up',
    request,
  );
  return data;
}

export async function fetchInvoices(): Promise<InvoiceListItem[]> {
  const { data } = await api.get<InvoiceListItem[]>('/api/billing/invoices');
  return data;
}

// -- Company / account ---------------------------------------------------------

export async function fetchCompany(upgraded = false): Promise<CompanyResponse> {
  const { data } = await api.get<CompanyResponse>('/api/company', { params: { upgraded } });
  return data;
}

export async function createCardCheckoutSession(): Promise<CheckoutSessionResponse> {
  const { data } = await api.post<CheckoutSessionResponse>('/api/company/card/checkout-session');
  return data;
}

export async function removeCard(): Promise<SimpleOperationResponse> {
  const { data } = await api.delete<SimpleOperationResponse>('/api/company/card');
  return data;
}

export async function createUpgradeCheckoutSession(): Promise<CheckoutSessionResponse> {
  const { data } = await api.post<CheckoutSessionResponse>('/api/company/upgrade/checkout-session');
  return data;
}

// -- API keys ------------------------------------------------------------------

export async function fetchApiKeys(): Promise<ApiKeyListItem[]> {
  const { data } = await api.get<ApiKeyListItem[]>('/api/api-keys');
  return data;
}

export async function createApiKey(
  request: CreateApiKeyRequest,
): Promise<ApiKeyOperationResponse> {
  const { data } = await api.post<ApiKeyOperationResponse>('/api/api-keys', request);
  return data;
}

export async function rotateApiKey(id: number): Promise<ApiKeyOperationResponse> {
  const { data } = await api.post<ApiKeyOperationResponse>(`/api/api-keys/${id}/rotate`);
  return data;
}

export async function revokeApiKey(id: number): Promise<ApiKeyOperationResponse> {
  const { data } = await api.post<ApiKeyOperationResponse>(`/api/api-keys/${id}/revoke`);
  return data;
}

// -- Notification preferences --------------------------------------------------

/** The signed-in client's own preferences, or (SuperAdmin) any client's. */
function notificationsBase(clientId?: number): string {
  return clientId === undefined ? '/api/notifications' : `/api/clients/${clientId}/notifications`;
}

export async function fetchNotificationPreferences(clientId?: number): Promise<NotificationPreferences> {
  const { data } = await api.get<NotificationPreferences>(notificationsBase(clientId));
  return data;
}

export async function saveNotificationPreferences(
  prefs: NotificationPreferences,
  clientId?: number,
): Promise<NotificationPreferences> {
  const { data } = await api.put<NotificationPreferences>(notificationsBase(clientId), prefs);
  return data;
}

// -- Platform settings (SuperAdmin) --------------------------------------------

export async function fetchPlatformSettings(): Promise<PlatformSettingsResponse> {
  const { data } = await api.get<PlatformSettingsResponse>('/api/settings');
  return data;
}

export async function saveBillingSettings(
  settings: BillingSettings,
): Promise<PlatformSettingsResponse> {
  const { data } = await api.put<PlatformSettingsResponse>('/api/settings/billing', settings);
  return data;
}

export async function saveEmailSettings(
  settings: EmailSettings,
): Promise<PlatformSettingsResponse> {
  const { data } = await api.put<PlatformSettingsResponse>('/api/settings/email', settings);
  return data;
}

export async function saveSecuritySettings(
  settings: SecuritySettings,
): Promise<PlatformSettingsResponse> {
  const { data } = await api.put<PlatformSettingsResponse>('/api/settings/security', settings);
  return data;
}

export async function saveFaceMatchSettings(
  settings: FaceMatchSettings,
): Promise<PlatformSettingsResponse> {
  const { data } = await api.put<PlatformSettingsResponse>('/api/settings/face-match', settings);
  return data;
}

export async function fetchDecisionRules(): Promise<DecisionRule[]> {
  const { data } = await api.get<DecisionRule[]>('/api/settings/decision-rules');
  return data;
}

export async function saveDecisionRules(switches: Record<string, boolean>): Promise<DecisionRule[]> {
  const { data } = await api.put<DecisionRule[]>('/api/settings/decision-rules', { switches });
  return data;
}

/** A client's Fraud Rules: its own (no id) or, for a SuperAdmin, one client's. */
export async function fetchClientFraudRules(clientId?: number): Promise<ClientDecisionRule[]> {
  const url = clientId === undefined ? '/api/company/fraud-rules' : `/api/clients/${clientId}/fraud-rules`;
  const { data } = await api.get<ClientDecisionRule[]>(url);
  return data;
}

export async function saveClientFraudRules(
  switches: Record<string, boolean>,
  clientId?: number,
): Promise<ClientDecisionRule[]> {
  const url = clientId === undefined ? '/api/company/fraud-rules' : `/api/clients/${clientId}/fraud-rules`;
  const { data } = await api.put<ClientDecisionRule[]>(url, { switches });
  return data;
}

// -- Plans (SuperAdmin) --------------------------------------------------------

export async function fetchPlans(): Promise<PlanListItem[]> {
  const { data } = await api.get<PlanListItem[]>('/api/plans');
  return data;
}

export async function fetchPlan(id: number): Promise<PlanListItem> {
  const { data } = await api.get<PlanListItem>(`/api/plans/${id}`);
  return data;
}

export async function updatePlan(id: number, request: UpdatePlanRequest): Promise<PlanListItem> {
  const { data } = await api.put<PlanListItem>(`/api/plans/${id}`, request);
  return data;
}

// -- Clients (admin) -----------------------------------------------------------
//
// The Razor portal spreads this over five pages; the SPA has two routes and does the forms in
// dialogs, so these are grouped by client id rather than by page.

export async function fetchClients(params: {
  page?: number;
  pageSize?: number;
  status?: string;
  search?: string;
  /** Live access: Pending, Approved or Rejected. */
  access?: string;
}): Promise<ClientListResponse> {
  const { data } = await api.get<ClientListResponse>('/api/clients', { params });
  return data;
}

export async function fetchClient(id: number): Promise<ClientDetail> {
  const { data } = await api.get<ClientDetail>(`/api/clients/${id}`);
  return data;
}

/** The Custom plan's defaults (Admin → Plans), for a client that does not exist yet. */
export async function fetchCustomPlanDefaults(): Promise<CustomPlanTerms> {
  const { data } = await api.get<CustomPlanTerms>('/api/clients/custom-terms/defaults');
  return data;
}

export async function fetchClientCustomTerms(id: number): Promise<CustomPlanTerms> {
  const { data } = await api.get<CustomPlanTerms>(`/api/clients/${id}/custom-terms`);
  return data;
}

export async function fetchClientInvoices(id: number): Promise<InvoiceListItem[]> {
  const { data } = await api.get<InvoiceListItem[]>(`/api/clients/${id}/invoices`);
  return data;
}

export async function createClient(request: CreateClientRequest): Promise<ClientDetail> {
  const { data } = await api.post<ClientDetail>('/api/clients', request);
  return data;
}

export async function updateClient(
  id: number,
  request: UpdateClientRequest,
): Promise<ClientOperationResponse> {
  const { data } = await api.put<ClientOperationResponse>(`/api/clients/${id}`, request);
  return data;
}

export async function changeClientStatus(
  id: number,
  request: ChangeClientStatusRequest,
): Promise<ClientOperationResponse> {
  const { data } = await api.post<ClientOperationResponse>(`/api/clients/${id}/status`, request);
  return data;
}

export async function changeClientPlan(
  id: number,
  request: ChangeClientPlanRequest,
): Promise<ClientOperationResponse> {
  const { data } = await api.post<ClientOperationResponse>(`/api/clients/${id}/plan`, request);
  return data;
}

export async function createClientApiKey(
  id: number,
  request: { name: string; environment: string },
): Promise<ClientApiKeyOperationResponse> {
  const { data } = await api.post<ClientApiKeyOperationResponse>(
    `/api/clients/${id}/api-keys`,
    request,
  );
  return data;
}

export async function rotateClientApiKey(
  id: number,
  keyId: number,
): Promise<ClientApiKeyOperationResponse> {
  const { data } = await api.post<ClientApiKeyOperationResponse>(
    `/api/clients/${id}/api-keys/${keyId}/rotate`,
  );
  return data;
}

export async function revokeClientApiKey(
  id: number,
  keyId: number,
): Promise<ClientApiKeyOperationResponse> {
  const { data } = await api.post<ClientApiKeyOperationResponse>(
    `/api/clients/${id}/api-keys/${keyId}/revoke`,
  );
  return data;
}

export async function adjustClientCredit(
  id: number,
  request: { amount: number; reason: string },
): Promise<ClientCreditOperationResponse> {
  const { data } = await api.post<ClientCreditOperationResponse>(
    `/api/clients/${id}/credit-adjustment`,
    request,
  );
  return data;
}

/**
 * Both of these return a URL for the browser to visit. Assign it to window.location - a fetch()
 * cannot follow the redirect, because the response is cross-origin and opaque.
 */
export async function startClientUpgradeCheckout(id: number): Promise<CheckoutSessionResponse> {
  const { data } = await api.post<CheckoutSessionResponse>(
    `/api/clients/${id}/upgrade-checkout`,
    null,
    { params: { returnTo: window.location.origin } },
  );
  return data;
}

export async function startClientCardCheckout(id: number): Promise<CheckoutSessionResponse> {
  const { data } = await api.post<CheckoutSessionResponse>(
    `/api/clients/${id}/card-checkout`,
    null,
    { params: { returnTo: window.location.origin } },
  );
  return data;
}

export async function removeClientCard(id: number): Promise<SimpleOperationResponse> {
  const { data } = await api.delete<SimpleOperationResponse>(`/api/clients/${id}/card`);
  return data;
}

// -- Passwords -----------------------------------------------------------------

/**
 * Self-service change. Takes no user id: the server acts on whoever the token says you are, so
 * there is nothing here that could be pointed at another account.
 */
export async function changePassword(
  request: ChangePasswordRequest,
): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>('/api/auth/change-password', request);
  // The change revokes every token issued before it, this one included; store the replacement
  // or the very next request is a 401.
  if (data.success && data.accessToken && data.expiresAt) setToken(data.accessToken, data.expiresAt);
  return data;
}

/**
 * Administrator reset. The generated password is emailed to the user and deliberately not
 * returned - it would otherwise pass through logs, proxies and browser history.
 */
export async function resetUserPassword(id: number): Promise<PasswordOperationResponse> {
  const { data } = await api.post<PasswordOperationResponse>(`/api/users/${id}/reset-password`);
  return data;
}

// -- My account ----------------------------------------------------------------

export async function fetchMyProfile(): Promise<MyProfile> {
  const { data } = await api.get<MyProfile>('/api/account/profile');
  return data;
}

export async function updateMyProfile(request: UpdateMyProfileRequest): Promise<MyProfile> {
  const { data } = await api.put<MyProfile>('/api/account/profile', request);
  return data;
}

// -- Contact us (anonymous) ------------------------------------------------------

export interface ContactRequest {
  name: string;
  companyName: string;
  email: string;
  useCase: string;
  /** Honeypot - always empty from a person. */
  website: string;
}

/** Emails the sales inbox. 400 carries `errors` keyed by field; 503 means it could not be sent. */
export async function submitContact(request: ContactRequest): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>('/api/contact', request);
  return data;
}

// -- Self-service signup (anonymous) -------------------------------------------

export async function fetchSignupOptions(): Promise<SignupOptions> {
  const { data } = await api.get<SignupOptions>('/api/signup/options');
  return data;
}

export async function submitSignup(request: SignupRequest): Promise<SignupResponse> {
  const { data } = await api.post<SignupResponse>('/api/signup', request);
  return data;
}

export async function completeSignup(sessionId: string): Promise<SignupCompletion> {
  const { data } = await api.get<SignupCompletion>('/api/signup/complete', {
    params: { sessionId },
  });
  return data;
}

export async function cancelSignup(clientId: number): Promise<void> {
  await api.post('/api/signup/cancel', { clientId });
}

// -- Live access: business verification, Beta Live, threshold ------------------

export async function submitKyb(request: KybSubmitRequest): Promise<OperationMessageResponse> {
  const { data } = await api.post<OperationMessageResponse>('/api/company/kyb', request);
  return data;
}

export async function updateCompanyContact(request: {
  contactName: string | null;
  contactEmail: string;
  contactPhone: string;
}): Promise<OperationMessageResponse> {
  const { data } = await api.put<OperationMessageResponse>('/api/company/contact', request);
  return data;
}

export async function updateCompanyThreshold(threshold: number | null): Promise<OperationMessageResponse> {
  const { data } = await api.put<OperationMessageResponse>('/api/company/threshold', { threshold });
  return data;
}

export async function updateCompanyFaceMatchThreshold(
  sFace: number | null,
  neural: number | null,
): Promise<OperationMessageResponse> {
  const { data } = await api.put<OperationMessageResponse>('/api/company/face-match-threshold', { sFace, neural });
  return data;
}

export async function approveKyb(clientId: number): Promise<OperationMessageResponse> {
  const { data } = await api.post<OperationMessageResponse>(`/api/clients/${clientId}/kyb/approve`);
  return data;
}

export async function rejectKyb(clientId: number, reason: string): Promise<OperationMessageResponse> {
  const { data } = await api.post<OperationMessageResponse>(`/api/clients/${clientId}/kyb/reject`, { reason });
  return data;
}

export async function updateClientBeta(
  clientId: number,
  dailyLimit: number | null,
  endsAt: string | null,
): Promise<OperationMessageResponse> {
  const { data } = await api.put<OperationMessageResponse>(`/api/clients/${clientId}/beta`, { dailyLimit, endsAt });
  return data;
}

export async function updateClientThreshold(
  clientId: number,
  threshold: number | null,
): Promise<OperationMessageResponse> {
  const { data } = await api.put<OperationMessageResponse>(`/api/clients/${clientId}/threshold`, { threshold });
  return data;
}

// -- Integration (return URLs + webhook) -----------------------------------------
// Without a clientId: the signed-in ClientAdmin's own client (/api/integration). With one: any
// client, SuperAdmin only (/api/clients/{id}/integration). Same payloads, same Core service.

function integrationBase(clientId?: number): string {
  return clientId === undefined ? '/api/integration' : `/api/clients/${clientId}/integration`;
}

export async function fetchIntegrationSettings(clientId?: number): Promise<IntegrationSettings> {
  const { data } = await api.get<IntegrationSettings>(integrationBase(clientId));
  return data;
}

export async function saveIntegrationSettings(
  settings: IntegrationSettingsInput,
  clientId?: number,
): Promise<IntegrationSettings> {
  const { data } = await api.put<IntegrationSettings>(integrationBase(clientId), settings);
  return data;
}

export async function regenerateWebhookSecret(clientId?: number): Promise<IntegrationSettings> {
  const { data } = await api.post<IntegrationSettings>(`${integrationBase(clientId)}/webhook-secret`);
  return data;
}

// -- Branding (verify-app and SDK look) -------------------------------------------
// Without a clientId: the signed-in user's own client (/api/branding; changes ClientAdmin only).
// With one: any client, SuperAdmin only (/api/clients/{id}/branding). Same Core service.

function brandingBase(clientId?: number): string {
  return clientId === undefined ? '/api/branding' : `/api/clients/${clientId}/branding`;
}

export async function fetchBranding(clientId?: number): Promise<BrandingSettings> {
  const { data } = await api.get<BrandingSettings>(brandingBase(clientId));
  return data;
}

export async function saveBranding(input: BrandingInput, clientId?: number): Promise<BrandingSettings> {
  const { data } = await api.put<BrandingSettings>(brandingBase(clientId), input);
  return data;
}

/** The current logo as a blob (bearer-protected, so it can't be an <img src>). */
export async function fetchBrandingLogo(clientId?: number): Promise<Blob> {
  const { data } = await api.get<Blob>(`${brandingBase(clientId)}/logo`, { responseType: 'blob' });
  return data;
}

export async function uploadBrandingLogo(file: File, clientId?: number): Promise<BrandingSettings> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<BrandingSettings>(`${brandingBase(clientId)}/logo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function removeBrandingLogo(clientId?: number): Promise<BrandingSettings> {
  const { data } = await api.delete<BrandingSettings>(`${brandingBase(clientId)}/logo`);
  return data;
}

// -- Public verification demo (anonymous) --------------------------------------

export type PublicDemoPurpose = 'identity-quick' | 'identity-full' | 'age' | 'barcode-only';

export interface PublicDemoStartRequest {
  purpose: PublicDemoPurpose;
  /** Age purpose only: a key of PublicDemoService.AgeFlows (Core). */
  ageFlow?: string;
  ageLimit?: number;
  /** Optional challenges on top of the centre selfie; the server always adds "blink". */
  livenessChallenges?: string[];
  sendSms: boolean;
  phone?: string;
}

export interface PublicDemoStartResponse {
  verificationId: string;
  link: string;
  smsDelivered: boolean;
  expiresAt: string | null;
  steps: string[];
}

/** As the verification API reports it. Fields are masked: the demo runs on a test key. */
export interface PublicDemoStatus {
  status: string;
  final: boolean;
  mode: string;
  decision: string | null;
  ageLimit: number | null;
  agePassed: boolean | null;
  reasons: string[];
  fields: Record<string, string>;
}

/**
 * Whether the verification demo is configured and this user may run it: a visitor must sign up,
 * and a client account needs an active test API key (its sessions run in its own sandbox).
 */
export async function fetchPublicDemoAccess(): Promise<{ available: boolean; signedIn: boolean; hasTestKey: boolean }> {
  const { data } = await api.get<{ available: boolean; signedIn: boolean; hasTestKey: boolean }>(
    '/api/public-demo/status',
  );
  return data;
}

export async function startPublicDemo(request: PublicDemoStartRequest): Promise<PublicDemoStartResponse> {
  const { data } = await api.post<PublicDemoStartResponse>('/api/public-demo/sessions', request);
  return data;
}

export async function fetchPublicDemoStatus(verificationId: string): Promise<PublicDemoStatus> {
  const { data } = await api.get<PublicDemoStatus>(`/api/public-demo/sessions/${encodeURIComponent(verificationId)}`);
  return data;
}

// -- Support (FEATURES.md 8e) -----------------------------------------------------

export interface SupportTicketQuery {
  page?: number;
  pageSize?: number;
  /** active (default), closed, all; team also needs-reply, unassigned, mine. */
  view?: string;
  category?: string;
  /** Team only. */
  source?: string;
  /** Team only. */
  clientId?: number;
  search?: string;
}

export async function fetchSupportTickets(query: SupportTicketQuery): Promise<SupportTicketListResponse> {
  const { data } = await api.get<SupportTicketListResponse>('/api/support/tickets', { params: query });
  return data;
}

/** Opening a request marks it read for the caller's side. */
export async function fetchSupportTicket(id: number): Promise<SupportTicketDetail> {
  const { data } = await api.get<SupportTicketDetail>(`/api/support/tickets/${id}`);
  return data;
}

/** multipart/form-data, so files can travel with the request. */
export async function createSupportTicket(request: CreateSupportTicketRequest, files: File[] = []): Promise<{ id: number }> {
  const form = new FormData();
  form.append('subject', request.subject);
  form.append('category', request.category);
  form.append('priority', request.priority);
  form.append('message', request.message);
  if (request.verificationSessionId) form.append('verificationSessionId', String(request.verificationSessionId));
  files.forEach((f) => form.append('files', f, f.name));
  const { data } = await api.post<{ id: number }>('/api/support/tickets', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function replyToSupportTicket(id: number, message: string, internalNote = false, files: File[] = []): Promise<void> {
  const form = new FormData();
  form.append('message', message);
  form.append('internalNote', String(internalNote));
  files.forEach((f) => form.append('files', f, f.name));
  await api.post(`/api/support/tickets/${id}/messages`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** An attachment's bytes through the authenticated client (images are bearer-protected, like document images). */
export async function fetchSupportAttachmentBlob(id: number): Promise<Blob> {
  const { data } = await api.get<Blob>(`/api/support/attachments/${id}`, { params: { download: true }, responseType: 'blob' });
  return data;
}

export async function updateSupportTicket(id: number, request: SupportTicketUpdateRequest): Promise<void> {
  await api.patch(`/api/support/tickets/${id}`, request);
}

export async function fetchSupportSummary(): Promise<SupportSummary> {
  const { data } = await api.get<SupportSummary>('/api/support/summary');
  return data;
}

export async function updateClientFaceMatchThreshold(
  clientId: number,
  sFace: number | null,
  neural: number | null,
): Promise<OperationMessageResponse> {
  const { data } = await api.put<OperationMessageResponse>(`/api/clients/${clientId}/face-match-threshold`, {
    sFace,
    neural,
  });
  return data;
}
